// src/pages/AdminPanel/EditProduct.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase'; // Ensure correct path
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from 'react-router-dom';
import { Package, Plus, X } from 'lucide-react'; // Icons
import './EditProduct.css';

const EditProduct = () => {
  const { productId } = useParams(); // Get product ID from URL params
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '', // Store as string for input, convert to number (cents) for Firestore
    stock: '', // Total stock
    imageUrl: '',
    category: '',
    brand: '',
    isActive: true,
    sizes: [], // Array of { size: string, stock: number }
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [productNotFound, setProductNotFound] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!productId) {
        setProductNotFound(true);
        setInitialLoading(false);
        return;
      }

      try {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
          const data = productSnap.data();
          setFormData({
            name: data.name || '',
            description: data.description || '',
            price: (data.price / 100).toFixed(2) || '', // Convert cents to dollars for display
            stock: data.stock || '', // Total stock (if separate field)
            imageUrl: data.imageUrl || '',
            category: data.category || '',
            brand: data.brand || '',
            isActive: data.isActive !== undefined ? data.isActive : true,
            sizes: data.sizes || [], // Initialize with existing sizes
          });
        } else {
          setProductNotFound(true);
        }
      } catch (error) {
        console.error("Error fetching product for edit:", error);
        Swal.fire('Error!', 'Failed to load product data.', 'error');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSizeChange = (index, field, value) => {
    const newSizes = [...formData.sizes];
    if (field === 'stock') {
      newSizes[index][field] = parseInt(value, 10) || 0; // Ensure stock is a number
    } else {
      newSizes[index][field] = value;
    }
    setFormData(prev => ({ ...prev, sizes: newSizes }));
  };

  const addSize = () => {
    setFormData(prev => ({
      ...prev,
      sizes: [...prev.sizes, { size: '', stock: 0 }]
    }));
  };

  const removeSize = (index) => {
    const newSizes = formData.sizes.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, sizes: newSizes }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Product name is required.';
    if (!formData.description) newErrors.description = 'Description is required.';
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required.';
    }
    // If total stock is used, validate it
    if (typeof formData.stock !== 'undefined' && (isNaN(formData.stock) || parseInt(formData.stock) < 0)) {
      newErrors.stock = 'Valid total stock quantity is required.';
    }
    if (!formData.imageUrl) newErrors.imageUrl = 'Image URL is required.';
    if (!formData.category) newErrors.category = 'Category is required.';
    if (!formData.brand) newErrors.brand = 'Brand is required.';
    
    // Validate individual sizes and stock
    formData.sizes.forEach((sizeEntry, index) => {
      if (!sizeEntry.size) newErrors[`size-${index}`] = `Size for row ${index + 1} is required.`;
      if (isNaN(sizeEntry.stock) || sizeEntry.stock < 0) newErrors[`stock-${index}`] = `Stock for size ${sizeEntry.size || (index + 1)} must be a non-negative number.`;
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      Swal.fire({
        title: 'Validation Error',
        text: 'Please fill in all required fields and correct any errors.',
        icon: 'error',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      });
      return;
    }

    setIsLoading(true);
    try {
      const productDataToUpdate = {
        ...formData,
        price: parseFloat(formData.price) * 100, // Convert dollars to cents
        stock: parseInt(formData.stock, 10), // Convert total stock to number
        sizes: formData.sizes.map(s => ({ // Ensure sizes' stock is numbers
          size: s.size, 
          stock: parseInt(s.stock, 10) || 0 
        })),
        updatedAt: serverTimestamp(), // Update timestamp on edit
      };

      const productRef = doc(db, 'products', productId);
      await updateDoc(productRef, productDataToUpdate);

      Swal.fire({
        title: 'Success!',
        text: 'Product updated successfully!',
        icon: 'success',
        confirmButtonText: 'OK',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      }).then(() => {
        navigate('/admin/products'); // Redirect to product list
      });

    } catch (error) {
      console.error("Error updating product:", error);
      Swal.fire({
        title: 'Error!',
        text: `Failed to update product: ${error.message}`,
        icon: 'error',
        background: '#1a202c',
        color: '#e2e8f0',
        customClass: { popup: 'swal2-popup-custom', title: 'swal2-title-custom' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper components for consistent form styling
  const InputField = ({ label, name, type = 'text', placeholder, value, onChange, error, ...props }) => (
    <div className="edit-product-form-group">
      <label htmlFor={name} className="edit-product-form-label">{label}</label>
      <input
        type={type}
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className="edit-product-form-input"
        {...props}
      />
      {error && <p className="edit-product-input-error-message">{error}</p>}
    </div>
  );

  const TextAreaField = ({ label, name, placeholder, value, onChange, error, ...props }) => (
    <div className="edit-product-form-group">
      <label htmlFor={name} className="edit-product-form-label">{label}</label>
      <textarea
        id={name}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows="4"
        className="edit-product-form-input edit-product-form-textarea"
        {...props}
      ></textarea>
      {error && <p className="edit-product-input-error-message">{error}</p>}
    </div>
  );

  const SelectField = ({ label, name, value, onChange, error, options, ...props }) => (
    <div className="edit-product-form-group">
      <label htmlFor={name} className="edit-product-form-label">{label}</label>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        className="edit-product-form-input edit-product-form-select"
        {...props}
      >
        <option value="" disabled>Select {label}</option>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      {error && <p className="edit-product-input-error-message">{error}</p>}
    </div>
  );

  if (initialLoading) return <div className="text-center py-10 text-gray-300 text-xl">Loading Product Data...</div>;
  if (productNotFound) return <div className="text-center py-10 text-red-400 text-xl">Product not found!</div>;

  return (
    <div className="edit-product-container-main"> {/* Main container for page-level styling */}
      <h2 className="section-title">Edit Product</h2> {/* Consistent title styling */}
      <form onSubmit={handleSubmit} className="product-edit-form"> {/* Custom class for form */}
        <InputField 
          label="Product Name" 
          name="name" 
          placeholder="e.g., Luxury Silk Scarf" 
          value={formData.name} 
          onChange={handleChange} 
          error={errors.name} 
          required 
        />
        <TextAreaField 
          label="Description" 
          name="description" 
          placeholder="Detailed description of the product..." 
          value={formData.description} 
          onChange={handleChange} 
          error={errors.description} 
          required 
        />
        <InputField 
          label="Price (USD)" 
          name="price" 
          type="number" 
          placeholder="e.g., 250.00" 
          value={formData.price} 
          onChange={handleChange} 
          error={errors.price} 
          step="0.01" 
          required 
        />
        <InputField 
          label="Total Stock Quantity" 
          name="stock" 
          type="number" 
          placeholder="e.g., 50" 
          value={formData.stock} 
          onChange={handleChange} 
          error={errors.stock} 
          required 
        />
        <InputField 
          label="Image URL" 
          name="imageUrl" 
          placeholder="https://example.com/product-image.jpg" 
          value={formData.imageUrl} 
          onChange={handleChange} 
          error={errors.imageUrl} 
          required 
        />
        {formData.imageUrl && (
          <div className="edit-product-image-preview-wrapper">
            <label className="edit-product-form-label">Current Image Preview:</label>
            <img src={formData.imageUrl} alt="Product Preview" className="edit-product-image-preview" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x150/333/fff?text=No+Img'; }} />
          </div>
        )}

        <SelectField
          label="Category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          error={errors.category}
          required
          options={[
            { value: '', label: 'Select a Category' },
            { value: 'Apparel', label: 'Apparel' },
            { value: 'Accessories', label: 'Accessories' },
            { value: 'Footwear', label: 'Footwear' },
            { value: 'Jewelry', label: 'Jewelry' },
            { value: 'Suits', label: 'Suits' },
          ]}
        />
        <SelectField
          label="Brand"
          name="brand"
          value={formData.brand}
          onChange={handleChange}
          error={errors.brand}
          required
          options={[
            { value: '', label: 'Select a Brand' },
            { value: 'Louis Vuitton', label: 'Louis Vuitton' },
            { value: 'Supreme', label: 'Supreme' },
            { value: 'Chrome Hearts', label: 'Chrome Hearts' },
          ]}
        />
        
        {/* Dynamic Size & Stock Input Section */}
        <div className="edit-product-sizes-stock-section">
          <h3 className="edit-product-sizes-stock-title">Product Sizes & Stock</h3>
          {formData.sizes.map((sizeEntry, index) => (
            <div key={index} className="edit-product-size-stock-entry">
              <InputField
                label={`Size ${index + 1}`}
                name={`size-${index}`}
                placeholder="e.g., S, M, L, XL, One-Size"
                value={sizeEntry.size}
                onChange={(e) => handleSizeChange(index, 'size', e.target.value)}
                error={errors[`size-${index}`]}
                required
              />
              <InputField
                label={`Stock for ${sizeEntry.size || `Size ${index + 1}`}`}
                name={`stock-${index}`}
                type="number"
                placeholder="Stock"
                value={sizeEntry.stock}
                onChange={(e) => handleSizeChange(index, 'stock', e.target.value)}
                error={errors[`stock-${index}`]}
                required
                min="0"
              />
              <button 
                type="button" 
                onClick={() => removeSize(index)} 
                className="edit-product-remove-size-button"
              >
                <X size={20} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addSize} className="edit-product-add-size-button">
            <Plus size={20} className="mr-2" /> Add Size Option
          </button>
        </div>


        <div className="edit-product-form-group flex items-center mt-6">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="edit-product-form-checkbox"
          />
          <label htmlFor="isActive" className="ml-2 block text-base text-gray-300">
            Product is Active
          </label>
        </div>

        <div className="edit-product-form-actions">
          <button 
            type="submit" 
            disabled={isLoading}
            className="edit-product-submit-button"
          >
            {isLoading ? 'Updating Product...' : 'Update Product'}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/products')} 
            className="edit-product-cancel-button"
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProduct;
